import {
	Checkbox,
	Dialog,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	Grid2,
	TextField,
	Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { ErrorModal } from "../../../../../components/modals/error-modal";
import { CenteredLazyLoading } from "../../../../../components/shared/CenteredLazyLoading";
import { HelmetDexteel } from "../../../../../helmet/HelmetDexteel";
import { ASSET_INITIAL_VALUES, Asset } from "../../../../../models/Asset";
import { useSearchAssets } from "../../hooks/useSearchAssets";

type Props = {
	selectedAssetId: number | null;
	show: boolean;
	onHide: (onHide: boolean) => void;
	suffixTitle?: string;
};

export const ViewerAssetDialog = ({
	selectedAssetId,
	show,
	onHide,
	suffixTitle,
}: Props) => {
	const [error, setError] = useState("");

	const {
		data: rows,
		isLoading,
		isError,
		error: e,
		isSuccess,
	} = useSearchAssets();

	const asset =
		rows && rows.length > 0
			? rows.find((a) => a.AssetId === selectedAssetId)
			: null;

	const { control, reset, setValue } = useForm<Asset>({
		defaultValues: asset ? asset : ASSET_INITIAL_VALUES,
	});

	useEffect(() => {
		if (show && isSuccess) {
			reset();
			setValue("ParentAssetId", asset?.ParentAssetId ?? null);
			setValue("AssetName", asset?.AssetName ?? "");

			const parentAssetNode = rows?.find(
				(node: any) => node.AssetId === asset?.ParentAssetId,
			);

			setValue("ParentAssetName", parentAssetNode?.AssetName);
			setValue("IsBranch", asset?.IsBranch ?? false);
			setValue("CanBeDefaultAsset", asset?.CanBeDefaultAsset as boolean);
		}
	}, [show, selectedAssetId, isSuccess]);

	useEffect(() => {
		if (e && isError) {
			setError(e.message);
		}
	}, [e, isError]);

	return (
		<>
			{show && (
				<HelmetDexteel
					title={`View Asset${suffixTitle ? ` - ${suffixTitle}` : ""}`}
				/>
			)}
			<Dialog
				fullWidth
				maxWidth="sm"
				open={show}
				onClose={() => onHide(false)}
				disableEnforceFocus
				sx={{
					"& .input-group-text": {
						display: "none",
					},
					"& .MuiPaper-root": {
						height: "auto",
					},
					"& .MuiDialogContent-dividers": {
						borderBottom: "unset",
					},
				}}
			>
				{isLoading ? (
					<div
						style={{ minHeight: 300, alignItems: "center", display: "flex" }}
					>
						<CenteredLazyLoading />
					</div>
				) : (
					<>
						<DialogTitle>
							<Grid2 size={{ md: 12, xs: 12 }}>
								<Typography variant="h5">{asset?.AssetName}</Typography>
							</Grid2>
						</DialogTitle>
						<DialogContent dividers>
							<Grid2 container spacing={1} alignItems="center">
								<Grid2 size={{ md: 12, xs: 12 }}>
									<Controller
										name="AssetName"
										control={control}
										render={({
											field: { onChange, value },
											fieldState: { error },
										}) => (
											<>
												<TextField
													label="Asset Name"
													variant="outlined"
													error={!!error}
													helperText={error?.message}
													fullWidth
													margin="dense"
													autoComplete="off"
													onChange={onChange}
													value={value}
													disabled={true}
												/>
											</>
										)}
									/>
								</Grid2>
								<Grid2 container size={{ md: 12 }}>
									<Grid2 size={{ md: 12, xs: 12 }}>
										<Controller
											name="ParentAssetName"
											control={control}
											render={({
												field: { onChange, value },
												fieldState: { error },
											}) => (
												<>
													{value ? (
														<TextField
															label={"Parent Asset"}
															variant="outlined"
															error={!!error}
															helperText={error?.message}
															fullWidth
															margin="dense"
															autoComplete="off"
															onChange={onChange}
															value={value || ""}
															disabled={true}
														/>
													) : (
														<></>
													)}
												</>
											)}
										/>
									</Grid2>
									<Grid2 container>
										<Grid2 size={{ xs: 6, md: 6 }}>
											<Controller
												name="IsBranch"
												control={control}
												defaultValue={false}
												render={({ field }) => (
													<FormControlLabel
														sx={{ userSelect: "none" }}
														control={
															<Checkbox
																checked={field.value as boolean}
																onChange={(e) =>
																	field.onChange(e.target.checked)
																}
																name="IsBranch"
																color="primary"
																disabled={true}
															/>
														}
														label="Has Children"
													/>
												)}
											/>
										</Grid2>
										<Grid2 size={{ xs: 6, md: 6 }}>
											<Controller
												name="CanBeDefaultAsset"
												control={control}
												defaultValue={false}
												render={({ field }) => (
													<FormControlLabel
														sx={{ userSelect: "none" }}
														control={
															<Checkbox
																checked={field.value as boolean}
																onChange={(e) =>
																	field.onChange(e.target.checked)
																}
																name="CanBeDefaultAsset"
																color="primary"
																disabled={true}
															/>
														}
														label="Can be Default Asset"
													/>
												)}
											/>
										</Grid2>
									</Grid2>
								</Grid2>
							</Grid2>
						</DialogContent>
					</>
				)}
			</Dialog>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
